// Wires up the playback-speed popover element rendered by
// Voice::Playback::SpeedPopoverComponent. The HTML is fetched once
// (cached module-level) and cloned per-caller; listeners are attached
// against the `js-speed-*` class hooks. Returns a `setRate(rate)` so external
// callers can sync the UI when the rate changes elsewhere (e.g. video player
// `ratechange` event).
//
// Used by:
//   - app/javascript/common/components/videojs/custom_plugins/playback_rate_button.js
//   - app/components/voice/playback/pill_component_controller.js

const PRESET_RATES = [0.5, 0.7, 1.0, 1.2, 1.5, 1.7, 2.0, 2.5]
const MIN_RATE = PRESET_RATES[0]
// Safari and iOS cap video playback speed at 2.0x; anything higher silently
// snaps back, which is confusing. Detect via UA (Chrome on Apple sets both
// "Safari" and "Chrome", so exclude it).
const MAX_RATE =
  /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    ? 2.0
    : 2.5

let htmlPromise = null

function fetchHtml() {
  if (htmlPromise) return htmlPromise
  htmlPromise = fetch('/voice_speech/speed_popover', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok)
        throw new Error(`Speed popover load failed: ${response.status}`)
      return response.text()
    })
    .catch((error) => {
      htmlPromise = null
      throw error
    })
  return htmlPromise
}

export async function buildSpeedPopover({
  initialRate = 1.0,
  onRateChange,
} = {}) {
  const html = await fetchHtml()
  const wrapper = document.createElement('div')
  wrapper.innerHTML = html.trim()
  const el = wrapper.firstElementChild

  const speedEl = el.querySelector('.js-speed')
  const sliderEl = el.querySelector('.js-slider')
  const decreaseEl = el.querySelector('.js-decrease-speed')
  const increaseEl = el.querySelector('.js-increase-speed')
  // Adjust slider and preset buttons to the effective max for this browser;
  // remove out-of-range presets and keep only the connected ones in the array
  // so syncUi and event listeners never touch detached nodes.
  sliderEl.max = MAX_RATE
  const presetEls = Array.from(el.querySelectorAll('[data-rate]')).filter(
    (preset) => {
      if (parseFloat(preset.dataset.rate) > MAX_RATE) {
        preset.remove()
        return false
      }
      return true
    }
  )

  let currentRate = clamp(initialRate)

  const apply = (rate) => {
    const clamped = clamp(rate)
    if (clamped === currentRate) return
    currentRate = clamped
    syncUi()
    onRateChange?.(clamped)
  }

  const syncUi = () => {
    speedEl.innerText = `${currentRate.toFixed(2)}x`
    sliderEl.value = currentRate
    decreaseEl.disabled = currentRate <= MIN_RATE
    increaseEl.disabled = currentRate >= MAX_RATE
    presetEls.forEach((preset) => {
      preset.classList.toggle(
        'is-active',
        parseFloat(preset.dataset.rate) === currentRate
      )
    })
  }

  decreaseEl.addEventListener('click', (e) => {
    e.preventDefault()
    apply(currentRate - 0.1)
  })
  increaseEl.addEventListener('click', (e) => {
    e.preventDefault()
    apply(currentRate + 0.1)
  })
  sliderEl.addEventListener('input', (e) => apply(parseFloat(e.target.value)))
  presetEls.forEach((preset) => {
    preset.addEventListener('click', (e) => {
      e.preventDefault()
      apply(parseFloat(preset.dataset.rate))
    })
  })

  syncUi()

  return {
    el,
    setRate(rate) {
      apply(rate)
    },
    get rate() {
      return currentRate
    },
  }
}

function clamp(rate) {
  const rounded = Math.round(rate * 10) / 10
  return Math.max(MIN_RATE, Math.min(MAX_RATE, rounded))
}

// One decimal max, trailing ".0" trimmed. Safari's `playbackRate()` returns
// floats like 1.7000000476837158, so callers must always format before display.
export function formatRate(rate) {
  const fixed = rate.toFixed(1)
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
}
